'use client';
import { useState } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import Link from '@/components/ui/Link';

interface Product {
    id: number;
    image: string;
    alt: string;
    name: string;
    price: string;
    originalPrice?: string;
    rating: number;
    reviews: number;
    isNew?: boolean;
    category: string;
    imgClass: string;
}

const newProducts: Product[] = [
    { id: 1, image: '/images/img_image.webp', alt: 'Yves Saint classic shirt', name: 'Yves Saint Classic', price: '₹39.99', rating: 5, reviews: 128, isNew: true, category: 'Men', imgClass: 'object-cover object-[center_top]' },
    { id: 2, image: '/images/img_image_382x336.webp', alt: 'Yves Saint premium sunglasses', name: 'Yves Saint Sunglasses', price: '₹49.99', originalPrice: '₹69.99', rating: 4, reviews: 96, isNew: true, category: 'Bags', imgClass: 'object-cover object-center' },
    { id: 3, image: '/images/img_image_4.webp', alt: 'Yves Saint summer dress for women', name: 'Yves Saint Summer', price: '₹34.99', rating: 5, reviews: 210, isNew: true, category: 'Women', imgClass: 'object-cover object-[center_top]' },
    { id: 4, image: '/images/img_image_5.webp', alt: 'Yves Saint casual shoulder bag', name: 'Yves Saint Bag', price: '₹29.99', rating: 4, reviews: 74, isNew: true, category: 'Bags', imgClass: 'object-cover object-center' },
    { id: 5, image: '/images/img_image_6.webp', alt: 'Yves Saint luxury evening wear', name: 'Yves Saint Luxe', price: '₹69.99', rating: 5, reviews: 182, isNew: true, category: 'Women', imgClass: 'object-cover object-[center_top]' },
    { id: 6, image: '/images/img_rectangle_9.webp', alt: 'Yves Saint kids apparel collection', name: 'Yves Saint Kids', price: '₹24.99', rating: 4, reviews: 55, isNew: true, category: 'Kids', imgClass: 'object-cover object-[center_top]' },
    { id: 7, image: '/images/img_image_2.webp', alt: 'Yves Saint sport wear and shoes', name: 'Yves Saint Sport', price: '₹59.99', rating: 5, reviews: 143, isNew: true, category: 'Shoes', imgClass: 'object-cover object-[right_top]' },
    { id: 8, image: '/images/img_image_3.webp', alt: 'Yves Saint casual jacket', name: 'Yves Saint Jacket', price: '₹89.99', rating: 5, reviews: 67, isNew: true, category: 'Men', imgClass: 'object-cover object-[right_top]' },
];

const tabs = ['All', 'Men', 'Women', 'Kids', 'Shoes', 'Bags'];

const StarRating = () => (
    <div className="flex items-center gap-1 justify-center">
        {[...Array(5)].map((_, i) => (
            <img key={i} src="/images/img_frame_amber_500.svg" alt="" className="w-3.5 h-3.5" />
        ))}
    </div>
);

const sortOptions = ['Newest', 'Price: Low to High', 'Price: High to Low', 'Most Popular'];

export default function NewArrivalsPage() {
    const [activeTab, setActiveTab] = useState('All');
    const [sortBy, setSortBy] = useState('Newest');
    const [searchQuery, setSearchQuery] = useState('');
    const [wishlist, setWishlist] = useState<number[]>([]);
    const [addedCart, setAddedCart] = useState<number[]>([]);

    const toggleWishlist = (id: number) => {
        setWishlist(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
    };

    const handleAddToCart = (id: number) => {
        if (!addedCart.includes(id)) {
            setAddedCart(prev => [...prev, id]);
            setTimeout(() => {
                setAddedCart(prev => prev.filter(item => item !== id));
            }, 2000);
        }
    };

    const getNumericPrice = (priceStr: string) => {
        return parseFloat(priceStr.replace(/[^0-9.]/g, '')) || 0;
    };

    let filtered = newProducts.filter(p => {
        const matchesTab = activeTab === 'All' || p.category === activeTab;
        const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            p.category.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesTab && matchesSearch;
    });

    filtered = [...filtered].sort((a, b) => {
        if (sortBy === 'Price: Low to High') {
            return getNumericPrice(a.price) - getNumericPrice(b.price);
        }
        if (sortBy === 'Price: High to Low') {
            return getNumericPrice(b.price) - getNumericPrice(a.price);
        }
        if (sortBy === 'Most Popular') {
            return b.reviews - a.reviews;
        }
        return b.id - a.id;
    });

    return (
        <>
            <Header />
            <main className="w-full bg-background-tertiary min-h-screen">
                {/* Page Banner */}
                <div className="w-full bg-background-secondary py-10 sm:py-14 lg:py-[60px]">
                    <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-[60px]">
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2 text-sm text-text-opacity-60">
                                <Link href="/" ariaLabel="Home" className="hover:text-primary-brand transition-colors">Home</Link>
                                <span>/</span>
                                <span className="text-text-primary font-medium">New Arrivals</span>
                            </div>
                            <h1 className="text-4xl sm:text-5xl lg:text-7xl text-text-primary font-semibold leading-tight">
                                New Arrivals
                            </h1>
                            <p className="text-lg sm:text-xl text-text-opacity-60 font-normal mt-1">
                                Fresh styles, just landed — be the first to wear them
                            </p>
                        </div>
                    </div>
                </div>

                <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-[60px] pt-10 sm:pt-12 pb-16 lg:pb-24">
                    {/* Highlight Banner */}
                    <div className="w-full bg-background-secondary border border-border-light rounded-2xl p-6 sm:p-8 lg:p-10 mb-10 flex flex-col sm:flex-row justify-between items-center gap-4 shadow-sm">
                        <div>
                            <span className="text-primary-brand text-xs sm:text-sm font-bold uppercase tracking-widest">JUST DROPPED</span>
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-text-primary mt-1">New Collection 2026</h2>
                            <p className="text-text-opacity-60 text-sm sm:text-base mt-1">Explore the latest styles from top designers</p>
                        </div>
                        <Link href="/shop" ariaLabel="Shop new collection" className="flex-shrink-0 bg-primary-brand text-white font-semibold px-6 py-3.5 rounded-full hover:bg-primary-brand-dark transition-colors duration-200 text-sm sm:text-base shadow-sm">
                            Shop Now →
                        </Link>
                    </div>

                    {/* Category Tabs & Sort Row */}
                    <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-4 mb-8">
                        <div className="flex flex-wrap gap-2 sm:gap-3">
                            {tabs.map(tab => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`px-4 sm:px-5 py-2 rounded-full text-sm sm:text-base font-medium transition-all duration-200 border ${activeTab === tab
                                            ? 'bg-primary-brand text-white border-primary-brand shadow-sm'
                                            : 'bg-white text-text-primary border-border-light hover:border-primary-brand hover:text-primary-brand'
                                        }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>

                        {/* Search & Sort */}
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                            <div className="relative flex-1 sm:w-64">
                                <input
                                    type="text"
                                    placeholder="Search new arrivals..."
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    className="w-full border border-border-light rounded-lg pl-9 pr-8 py-2 text-sm text-text-primary bg-white focus:outline-none focus:border-primary-brand shadow-sm"
                                />
                                <svg className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery('')}
                                        className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600 text-xs bg-gray-100 rounded-full w-4 h-4 flex items-center justify-center"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>

                            <div className="flex items-center gap-2">
                                <span className="text-sm text-text-opacity-60 whitespace-nowrap">Sort by:</span>
                                <select
                                    value={sortBy}
                                    onChange={e => setSortBy(e.target.value)}
                                    className="border border-border-light rounded-lg px-3 py-2 text-sm font-medium text-text-primary bg-white focus:outline-none focus:border-primary-brand shadow-sm cursor-pointer"
                                >
                                    {sortOptions.map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Products Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
                        {filtered.map(product => (
                            <div key={product.id} className="group flex flex-col bg-white rounded-xl sm:rounded-2xl overflow-hidden border border-border-light/60 shadow-sm hover:shadow-lg transition-all duration-300">
                                {/* Edge-to-Edge Image Container (Remains still on hover) */}
                                <div className="relative overflow-hidden bg-[#f4f4f6] aspect-[4/5] w-full flex items-center justify-center">
                                    <img
                                        src={product.image}
                                        alt={product.alt}
                                        className={`w-full h-full ${product.imgClass}`}
                                    />

                                    {/* New Badge */}
                                    {product.isNew && (
                                        <span className="absolute top-3 left-3 bg-primary-brand text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm">
                                            New
                                        </span>
                                    )}

                                    {/* Wishlist Button */}
                                    <button
                                        onClick={() => toggleWishlist(product.id)}
                                        className="absolute top-3 right-3 w-9 h-9 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md transition-all duration-200 hover:scale-110 active:scale-95 cursor-pointer"
                                        aria-label="Add to wishlist"
                                    >
                                        <svg
                                            className={`w-4 h-4 transition-colors ${wishlist.includes(product.id) ? 'fill-red-500 stroke-red-500' : 'fill-none stroke-gray-700'}`}
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.684a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                        </svg>
                                    </button>
                                </div>

                                <div className="flex flex-col gap-2.5 p-4 sm:p-5 flex-1">
                                    <h3 className="text-base sm:text-lg font-semibold text-text-primary text-center line-clamp-1 group-hover:text-primary-brand transition-colors">
                                        {product.name}
                                    </h3>

                                    <StarRating />

                                    <span className="text-xs text-text-opacity-60 text-center">({product.reviews} reviews)</span>

                                    <div className="flex items-center justify-center gap-2 mt-1">
                                        <span className="text-base sm:text-lg font-bold text-primary-accent">{product.price}</span>
                                        {product.originalPrice && (
                                            <span className="text-sm text-text-opacity-60 line-through">{product.originalPrice}</span>
                                        )}
                                    </div>

                                    <button
                                        onClick={() => handleAddToCart(product.id)}
                                        className={`mt-3 w-full py-2.5 px-4 text-sm font-semibold rounded-lg transition-all duration-200 ${addedCart.includes(product.id)
                                                ? 'bg-emerald-600 text-white'
                                                : 'bg-primary-brand text-white hover:bg-primary-brand-dark active:scale-[0.98]'
                                            }`}
                                    >
                                        {addedCart.includes(product.id) ? 'Added to Cart ✓' : 'Add to Cart'}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </main>
            <Footer />
        </>
    );
}


